import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './miniapp.css';
import { API_BASE } from '../config';
import { ListingsSearchCriteriaApi, SurveyData } from '../services/listingsSearchCriteriaApi';
import { MiniAppApi, UnauthorizedError } from './miniAppApi';
import { initData, startTelegramApp } from './telegram';
import { SearchSummary } from './types';
import Feed from './feed/Feed';
import SearchWizard from './wizard/SearchWizard';

type View =
  | { kind: 'loading' }
  | { kind: 'unauthorized' }
  | { kind: 'failed' }
  | { kind: 'wizard' }
  | { kind: 'done'; searchId: string }
  | { kind: 'feed'; searchId?: string };

type Props = {
  api?: Pick<MiniAppApi, 'createSearch' | 'fetchSearches' | 'fetchListings'>;
  referenceApi?: Pick<ListingsSearchCriteriaApi, 'fetchLocations' | 'fetchPostcodes' | 'matchListings'>;
};

function CenteredMessage({ title, text, action }: { title: string; text: string; action?: React.ReactNode }) {
  return (
    <div className="ma-centered">
      <div>
        <h1 className="ma-title">{title}</h1>
        <p className="ma-subtitle">{text}</p>
        {action}
      </div>
    </div>
  );
}

export default function MiniApp({ api: givenApi, referenceApi: givenReferenceApi }: Props) {
  const api = useMemo(() => givenApi ?? new MiniAppApi(API_BASE, initData), [givenApi]);
  const referenceApi = useMemo(() => givenReferenceApi ?? new ListingsSearchCriteriaApi(API_BASE), [givenReferenceApi]);
  const [view, setView] = useState<View>({ kind: 'loading' });
  const [searches, setSearches] = useState<SearchSummary[]>([]);

  const handleError = useCallback((err: unknown) => {
    console.error('Mini app request failed', err);
    setView(err instanceof UnauthorizedError ? { kind: 'unauthorized' } : { kind: 'failed' });
  }, []);

  useEffect(() => {
    startTelegramApp();
    api.fetchSearches()
      .then(found => {
        setSearches(found);
        setView(found.length ? { kind: 'feed' } : { kind: 'wizard' });
      })
      .catch(handleError);
  }, [api, handleError]);

  const createSearch = useCallback(async (data: SurveyData) => {
    const { id } = await api.createSearch(data);
    try {
      setSearches(await api.fetchSearches());
      setView({ kind: 'done', searchId: id });
    } catch (err) {
      handleError(err);
    }
  }, [api, handleError]);

  const openWizard = useCallback(() => setView({ kind: 'wizard' }), []);
  const closeWizard = useCallback(() => setView({ kind: 'feed' }), []);

  return (
    <div className="ma-root">
      {view.kind === 'loading' && <CenteredMessage title="HomeScout" text="Loading…" />}
      {view.kind === 'unauthorized' && (
        <CenteredMessage title="Open HomeScout from Telegram" text="Please open this app from the HomeScout bot in Telegram." />
      )}
      {view.kind === 'failed' && (
        <CenteredMessage title="Something went wrong" text="We couldn't load your searches. Please try again later." />
      )}
      {view.kind === 'wizard' && (
        <SearchWizard
          referenceApi={referenceApi}
          onSubmit={createSearch}
          onCancel={searches.length ? closeWizard : undefined}
        />
      )}
      {view.kind === 'done' && (
        <CenteredMessage
          title="You're all set"
          text="We'll message you in the chat the moment a matching flat is listed."
          action={(
            <button
              type="button"
              className="ma-primary"
              onClick={() => setView({ kind: 'feed', searchId: view.searchId })}
            >
              See matching flats
            </button>
          )}
        />
      )}
      {view.kind === 'feed' && (
        <Feed api={api} searches={searches} initialSearchId={view.searchId} onNewSearch={openWizard} />
      )}
    </div>
  );
}
